import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  colors,
  Input,
  Tab,
  Tabs,
  useDisclosure,
} from '@nextui-org/react';
import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectLoggedIn } from '../store/auth/authSlice';
import { getSelf, login, register } from '../store/auth/authThunks';
import { generateGradients, loadJwt, MOTION_KEYS } from '../util';
import { GENERIC_APP } from '../config/generic';
import { SOS_APP } from '../config/sos';
import ResetModal from '../components/forgot-password/ResetModal';

const KEY_LOGIN = 'login';
const KEY_REGISTER = 'register';

export default function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(selectLoggedIn);

  const [selectedOption, setSelectedOption] = useState<string>('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [password, setPassword] = useState(''); // ✅ Controlled password

  const {
    isOpen: isResetOpen,
    onOpen: onResetOpen,
    onOpenChange: onResetOpenChange,
  } = useDisclosure();

  const name =
    process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC'
      ? GENERIC_APP.name
      : SOS_APP.name;

  const disclaimer =
    process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC'
      ? GENERIC_APP.disclaimer
      : SOS_APP.disclaimer;

  const handleInvalid = (e: React.FormEvent<HTMLInputElement>) => {
    e.currentTarget.setCustomValidity('Please enter a valid email');
  };

  const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
    e.currentTarget.setCustomValidity('');
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsEmailValid(regex.test(e.target.value));
  };

  useEffect(() => {
    const jwt = loadJwt();
    if (jwt) dispatch(getSelf());
  }, [dispatch]);

  useEffect(() => {
    if (isLoggedIn) navigate('/');
  }, [navigate, isLoggedIn]);

  const handleLogin = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = new FormData(e.currentTarget);
    const name = form.get('name')?.toString();
    const username = form.get('username')?.toString();

    switch (selectedOption) {
      case KEY_LOGIN:
        if (username && password) {
          dispatch(login({ username, password }));
        }
        break;
      case KEY_REGISTER:
        if (name && username && password) {
          dispatch(register({ name, username, password }));
        }
        break;
    }
  };

  return (
    <motion.div
      variants={{
        [MOTION_KEYS.INITIAL]: {},
        [MOTION_KEYS.ANIMATE]: {
          background: generateGradients([
            colors.red[200],
            colors.yellow[200],
            colors.green[200],
            colors.cyan[200],
            colors.blue[200],
            colors.purple[200],
          ]),
          transition: { duration: 60, repeat: Infinity, ease: 'linear' },
        },
        [MOTION_KEYS.EXIT]: {},
      }}
      initial={MOTION_KEYS.INITIAL}
      animate={MOTION_KEYS.ANIMATE}
      exit={MOTION_KEYS.EXIT}
      className="flex flex-col justify-center items-center w-screen h-screen"
    >
      <motion.div className="w-5/6 max-w-lg">
        <Card className="w-full h-full flex flex-col justify-center items-center py-8">
          <CardHeader className="flex flex-col justify-center py-16">
            <motion.p className="text-5xl font-semibold">{name}</motion.p>
          </CardHeader>

          <motion.form onSubmit={handleLogin} className="w-full">
            <CardBody className="flex flex-1 flex-col mx-auto gap-4 max-w-md">
              <Tabs
                color="primary"
                selectedKey={selectedOption}
                onSelectionChange={(k) => {
                  setSelectedOption(k.toString());
                  setPassword(''); // ✅ Clear password when tab changes
                }}
                aria-label="Login options"
                className="self-center"
              >
                <Tab key={KEY_LOGIN} title="Login" />
                <Tab key={KEY_REGISTER} title="Register" />
              </Tabs>

              {selectedOption === KEY_REGISTER && (
                <Input
                  autoFocus
                  isRequired
                  name="name"
                  type="text"
                  placeholder="Name"
                />
              )}

              {selectedOption === KEY_REGISTER && (
                <Input
                  isRequired
                  name="username"
                  type="email"
                  onInvalid={handleInvalid}
                  onInput={handleInput}
                  onChange={handleEmailChange}
                  placeholder="Email address"
                />
              )}

              {selectedOption === KEY_LOGIN && (
                <Input
                  autoFocus
                  isRequired
                  name="username"
                  type="username"
                  placeholder="User name"
                />
              )}

              {/* ✅ Controlled password input */}
              <Input
                isRequired
                name="password"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              {/* ✅ Forgot password link (only for login) */}
              {selectedOption === KEY_LOGIN && (
                <p
                  className="text-sm text-blue-500 cursor-pointer hover:underline text-right"
                  onClick={onResetOpen}
                >
                  Forgot your password?
                </p>
              )}

              <ResetModal
                isOpen={isResetOpen}
                onOpenChange={onResetOpenChange}
              />
            </CardBody>

            <CardFooter className="flex flex-col items-center gap-4">
              <Button
                type="submit"
                color="primary"
                disabled={selectedOption === KEY_REGISTER && !isEmailValid}
              >
                {selectedOption === KEY_REGISTER ? 'Register' : 'Login'}
              </Button>
              <p className="text-xs text-center">{disclaimer}</p>
            </CardFooter>
          </motion.form>
        </Card>
      </motion.div>
    </motion.div>
  );
}
